import FieldNames from '../defaults/FieldNames.json';
import * as query from "@arcgis/core/rest/query";
import Query from "@arcgis/core/rest/support/Query";
import Request from './request';
import FeatureService from './FeatureService';
import GeneralService from './GeneralService';
import { getAppConfig, getAttributeServiceUrl, getNifcToken } from '../utils/ReduxHelper';

const PlannedActualsService = {
  hasPlannedActualFeatures: function(geometryType, featureId) {
    try
    {
      const config = getAppConfig();
      const rootServiceUrl = getAttributeServiceUrl();
      const url = `${rootServiceUrl}/${config.estimatedActual[config.env].layerId}`;
      if(featureId){
        const queryObject = new Query({
          where: `(${FieldNames.estimatedActuals.joinIDE} = ${featureId}) AND (${FieldNames.estimatedActuals.joinFeatureE} = '${geometryType}')`,
          outFields: "*",
        });
        return query.executeQueryJSON(url, queryObject).then(results => {
          if(results && results.features && results.features.length > 0){
            return results.features[0];
          } else {
            return null;
          }
        });
      } else {
        throw Error("Missing feature object ID");
      }
    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },
  getAllPlannedActualRecords: function(whereClause){
    try
    {
      const config = getAppConfig();
      const rootServiceUrl = getAttributeServiceUrl();
      const queryObject = new Query({
          where: whereClause,
          outFields: "*",
          returnGeometry: false,
          returnDistinctValues: true,
      });
      let url = `${rootServiceUrl}/${config.estimatedActual[config.env].layerId}`;
      
      return GeneralService.queryCallDirect(url, queryObject).then(results => {
        if(results && results.features && results.features.length > 0){
          return results.features
        } else {
          return [];
        }
      });
    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },
  getAttachedFeatures: function(geometryType, idList, isEstimate){
    try
    {
      //This is required for adding items to groups that don't exist in the created shape or selected list
      if(!idList || idList.length === 0)
      {
        return new Promise((resolve) => {
          resolve([]);
        });
      }
      const joinIdType = isEstimate ? FieldNames.estimatedActuals.joinIDE : FieldNames.estimatedActuals.joinIDA;
      const joinFeatureType = isEstimate ? FieldNames.estimatedActuals.joinFeatureE : FieldNames.estimatedActuals.joinFeatureA;
      const idListConcat = idList.join(", ");
      const queryStr = `(${joinIdType} IN (${idListConcat})) AND (${joinFeatureType} = '${geometryType}')`

      return new Promise((resolve) => {
        Promise.allSettled([
          this.getAllPlannedActualRecords(queryStr),
        ]).then(joinedResults => {
          //Now that I have join results grab all plannedObjectIDs and actualObjectIDs
          const returnedPlannedActuals = (joinedResults && joinedResults[0] && joinedResults[0].status === "fulfilled") ? ((joinedResults[0].value && joinedResults[0].value.length > 0) ? joinedResults[0].value : []) : [];
          let featuresToLookup = [];
          if(returnedPlannedActuals?.length){
            returnedPlannedActuals.forEach(joinedEntry => {
              let joinedEntryAttr = joinedEntry.attributes;
              let idVal = joinedEntryAttr[FieldNames.estimatedActuals.joinIDA] ?? joinedEntryAttr[FieldNames.estimatedActuals.joinIDE];
              featuresToLookup.push(idVal);
            })
          }
  
          Promise.allSettled([
            (featuresToLookup?.length) ? FeatureService.getFeaturesByIDs(featuresToLookup) : null,
          ]).then(allResults => {
            //Now we have all records we are interested in
            let features = (allResults && allResults[0] && allResults[0].status === "fulfilled") ? ((allResults[0].value && allResults[0].value.length > 0) ? allResults[0].value : []) : [];
            let treatments = features?.filter(x => x[FieldNames.featureFields.class] === FieldNames.classTypes.treatment);
            let actualTreatments = features?.filter(x => x[FieldNames.featureFields.class] === FieldNames.classTypes.actualTreatment);
            let activities = features?.filter(x => x[FieldNames.featureFields.class] === FieldNames.classTypes.activity);
            let actualActivities = features?.filter(x => x[FieldNames.featureFields.class] === FieldNames.classTypes.actualActivity);
            let allFeatures = [
              ...treatments,
              ...actualTreatments,
              ...activities,
              ...actualActivities
            ]
            resolve(allFeatures)
          });
  
        });
      });
    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },
  getPlannedActualsByJoinIDs: function(geometryType, actualIDs, isActual)
  {
    try
    {
      const config = getAppConfig();
      const rootServiceUrl = getAttributeServiceUrl();
      if(actualIDs && actualIDs.length > 0){
        let whereArray = [];
          actualIDs.forEach((id) => {
            if(isActual){
              whereArray.push(`(${FieldNames.estimatedActuals.joinIDA} = ${id}) AND (${FieldNames.estimatedActuals.joinFeatureA} = '${geometryType}')`);
            } else {
              whereArray.push(`(${FieldNames.estimatedActuals.joinIDE} = ${id}) AND (${FieldNames.estimatedActuals.joinFeatureE} = '${geometryType}')`);
            }
          })
          const whereClause = whereArray.join(" OR ");
  
          const queryObject = new Query({
              where: whereClause,
              outFields: "*",
              returnGeometry: false
          });
          let url = `${rootServiceUrl}/${config.estimatedActual[config.env].layerId}`;
  
          return query.executeQueryJSON(url, queryObject).then(results => {
            return results.features;
          });
      } else {
        return new Promise((resolve) => {
          resolve(null);
        })
      }    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },
  createPlannedActualFeature: function (plannedActualItem) 
  {   
    try
    {
      const config = getAppConfig();
      const rootServiceUrl = getAttributeServiceUrl();
      const nifcToken = getNifcToken();
      const url = `${rootServiceUrl}/${config.estimatedActual[config.env].layerId}/addFeatures`;
      const newItem = 
      {
          attributes: plannedActualItem
      }
      return new Promise((resolve, reject) => {

          new Request().makeRequest({
              url: url,
              method: 'post',
              data: {
                  f: 'json',
                  token: nifcToken,
                  features: JSON.stringify([newItem])
              }
          }).then(
              resp => {
                  resolve(resp)
              },
              error => {
                reject(error)
              }
          );
          
      });
    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },
  updatePlannedActualFeature: function (plannedActualItem) 
  {   
    try
    {
      const config = getAppConfig();
      const rootServiceUrl = getAttributeServiceUrl();
      const nifcToken = getNifcToken();
      const url = `${rootServiceUrl}/${config.estimatedActual[config.env].layerId}/updateFeatures`;
      return new Promise((resolve, reject) => {
  
          new Request().makeRequest({
              url: url,
              method: 'post',
              data: {
                  f: 'json',
                  token: nifcToken,
                  features: JSON.stringify([plannedActualItem])
              }
          }).then(
              resp => {
                  resolve(resp)
              },
              error => {
                reject(error)
              }
          );
          
      });
    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },
};

export default PlannedActualsService;
